import React, {
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  Configure,
  connectStateResults,
  InstantSearch,
} from 'react-instantsearch-dom';
import { isEqual } from 'lodash';
import AdminDashboardFilters from '../widgets/filters/AdminDashboardFilters';
import DateRange from '../widgets/filters/DateRange';
import ClearRefinements from '../widgets/ClearRefinements';
import DebouncedSearchBox from '../widgets/DebouncedSearchBox';
import BulkSelectorMenu from '../widgets/BulkSelectorMenu';
import useScrollPage from '../../../utils/hooks/useScrollPage';
import CustomHitsPerPage from '../widgets/CustomHitsPerPage';
import { useAlgoliaSearchClientLite } from '../../../utils/hooks/useAlgoliaSearchClient';
import Pagination from './Pagination';
import AdminDashboardTableTabs from './AdminDashboardTableTabs';
import TableHeaderRow from './TableHeaderRow';
import {
  AdminDashboardStateResultsInterface,
  AdminDashboardTableInterface,
  DateAttributesInterface,
} from './interfaces';

const AdminDashboardStateResults: React.FC<AdminDashboardStateResultsInterface> = ({
  searchResults,
  setHits,
  setExtraData,
  clearBulkSelected,
}) => {
  const [currentHits, setCurrentHits] = useState<any[]>([]);
  // Save the hit ids for use in the bulkSelector hook
  useEffect(() => {
    const newHits = searchResults?.hits
      ? searchResults?.hits.map((hit: {id: number}) => hit.id)
      : [];
    if (!isEqual(newHits, currentHits)) {
      // First, clear the currently selected hits, if any
      if (clearBulkSelected) {
        clearBulkSelected();
      }
      setCurrentHits(newHits);
    }

    if (setExtraData) {
      setExtraData(searchResults?.hits || []);
    }
  }, [searchResults]);

  useEffect(() => {
    setHits(currentHits);
  }, [currentHits]);

  // Get page
  // NOTE: `page` is zero-based from algolia
  let page = 0;
  if (searchResults && searchResults.page) {
    page = searchResults.page;
  }

  // Get hits per page
  let hitsPerPage = 0;
  if (searchResults && searchResults.hitsPerPage) {
    hitsPerPage = searchResults.hitsPerPage;
  }

  // Get total number of hits
  let nbHits = 0;
  if (searchResults && searchResults.nbHits) {
    nbHits = searchResults.nbHits;
  }

  // Calculate first value in the range
  const beginRange = (page * hitsPerPage) + 1;

  // Calculate last value in the range
  let endRange = (page + 1) * hitsPerPage;
  if (endRange > nbHits) {
    endRange = nbHits;
  }

  if (nbHits === 0) {
    return (<p className="total-res my-4 mr-3 no-results">0 results found</p>);
  }

  return (
    /* NOTE: `toLocaleString()` adds comma for thousands, etc. */
    <p className="total-res my-4 mr-3">{`Showing ${beginRange} - ${endRange} of ${nbHits.toLocaleString('en-US')} ${nbHits === 1 ? 'result' : 'results'}`}</p>
  );
};

const CustomStateResults = connectStateResults(AdminDashboardStateResults);

const AdminDashboardTable: React.FC<AdminDashboardTableInterface> = ({
  title,
  currentUser,
  bulkAction,
  setBulkAction,
  externalQueryParams,
  indexName,
  defaultRefinement,
  setDefaultRefinement,
  tableHeaderRowValues,
  customHits,
  searchState,
  onSearchStateChange,
  createURL,
  facetFilters,
  setFacetFilters,
  numericFilters,
  setNumericFilters,
  attributes,
  dateAttributes,
  onReset,
  location,
  tabs,
  bulkActions,
  allSelected,
  setHits,
  showSearch,
  showReset,
  obscureSearch,
  setExtraData,
  toggleAllSelected,
  clearBulkSelected,
  useCards,
  allowSetHitsPerPage,
  ActionButton,
  TitleComponent,
  BodyComponent,
  numEmptyStartHeaders,
  numEmptyEndHeaders,
  numUnhiddenEmptyEndHeaders,
  allowBulkSelect,
  onQueryUpdateCallback,
  ExternalBulkSelector,
  scrollPage,
}): ReactElement => {
  const { searchClient } = useAlgoliaSearchClientLite(currentUser?.public_key);
  const [currentTab, setCurrentTab] = useState(location.pathname);

  useEffect(() => {
    const newDefaultRefinementTab = externalQueryParams.find((param) => param.key === 'defaultRefinement');
    if (newDefaultRefinementTab) {
      const newDefaultRefinement = newDefaultRefinementTab.value;
      if (newDefaultRefinement !== defaultRefinement) {
        if (setDefaultRefinement) {
          setDefaultRefinement(newDefaultRefinement);
        }
      }
    }
    const newTab = externalQueryParams
      .map((param) => `${param.key}=${param.value}`)
      .join('&');
    if (newTab !== currentTab) {
      setCurrentTab(newTab);
    }
  }, [externalQueryParams, currentTab, defaultRefinement, setDefaultRefinement]);

  const MemoizedAdminDashboardTableTabs = useCallback(() => (
    <AdminDashboardTableTabs
      tabs={tabs}
      currentTab={currentTab}
      searchClient={searchClient}
    />
  ), [tabs, currentTab, searchClient]);

  // @ts-ignore
  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={indexName}
      searchState={searchState}
      onSearchStateChange={onSearchStateChange}
      createURL={createURL}
    >
      <Configure
        facetFilters={facetFilters}
        numericFilters={numericFilters}
      />
      <div className="admin-dashboard-table-wrapper">
        <div className="row d-flex justify-content-between align-items-center border-bottom border-l-gray pb-2 mb-4">
          <div className="col-sm-8 col-md-5">
            {TitleComponent && <TitleComponent />}
            {title && (
              <h1 className="text-capitalize">{title}</h1>
            )}
          </div>
          <div className="col-sm-4 col-md-7 d-flex align-items-center flex-wrap justify-content-md-end">
            {ActionButton && <ActionButton />}
            {(bulkActions && setBulkAction) && (
              <BulkSelectorMenu
                actions={bulkActions}
                setBulkAction={setBulkAction}
                bulkAction={bulkAction}
              />
            )}
          </div>
        </div>
        {BodyComponent && <BodyComponent />}
        <div className="admin-filter-wrapper d-flex flex-wrap">
          {showSearch && <DebouncedSearchBox onUpdateCallback={onQueryUpdateCallback} />}
          {!!(!showSearch && obscureSearch) && (
            <DebouncedSearchBox
              onUpdateCallback={onQueryUpdateCallback}
              obscureSearch
            />
          )}
          <AdminDashboardFilters
            searchClient={searchClient}
            facetFilters={facetFilters}
            setFacetFilters={setFacetFilters}
            numericFilters={numericFilters}
            setNumericFilters={setNumericFilters}
            attributes={attributes}
            indexName={indexName}
          />
          {ExternalBulkSelector}
        </div>
        {tabs.length > 0 && <MemoizedAdminDashboardTableTabs />}
        <div className="row d-flex justify-content-between align-items-center py-3 py-sm-0">
          <div className="col-12 col-sm-6 col-md-7 d-flex align-items-center">
            {setHits && (
              <CustomStateResults
                setHits={setHits}
                setExtraData={setExtraData}
                clearBulkSelected={clearBulkSelected}
              />
            )}
            {showReset && (
              <ClearRefinements
                // @ts-ignore
                clearBulkSelected={clearBulkSelected}
                onReset={onReset}
              />
            )}
          </div>
          {allowSetHitsPerPage && (
            <div className="col-12 col-sm-6 col-md-5 d-flex align-items-center hits-pp-wrapper justify-content-center justify-content-sm-end">
              <p className="m-0 mr-2">{useCards ? 'Show:' : 'Results per page'}</p>
              <CustomHitsPerPage
                items={useCards
                  ? [
                    { value: 10, label: '10' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' },
                    { value: 100, label: '100' },
                  ] : [
                    { value: 50, label: '50' },
                    { value: 100, label: '100' },
                    { value: 200, label: '200' },
                  ]}
                defaultRefinement={useCards ? 25 : 50}
              />
            </div>
          )}
        </div>
        {
          dateAttributes.map((attr: DateAttributesInterface) => (
            <DateRange
              key={attr.attribute}
              attribute={attr.attribute}
              attributeObject={attr}
              numericFilters={numericFilters}
              setNumericFilters={setNumericFilters}
            />
          ))
        }
        {useCards ? (
          <>
            {customHits}
          </>
        ) : (
          <table className="table table-responsive-xl table-hover border">
            <tbody>
              {tableHeaderRowValues && (
                <TableHeaderRow
                  defaultRefinement={defaultRefinement || indexName}
                  rowValues={tableHeaderRowValues}
                  allSelected={allSelected}
                  toggleAllSelected={toggleAllSelected}
                  numEmptyStartHeaders={numEmptyStartHeaders}
                  numEmptyEndHeaders={numEmptyEndHeaders}
                  numUnhiddenEmptyEndHeaders={numUnhiddenEmptyEndHeaders}
                  allowBulkSelect={allowBulkSelect}
                />
              )}
              {customHits}
            </tbody>
          </table>
        )}
        <Pagination
          scrollPage={scrollPage || useScrollPage()}
          clearBulkSelected={clearBulkSelected}
        />
      </div>
    </InstantSearch>
  );
};

AdminDashboardTable.defaultProps = {
  title: '',
  allowSetHitsPerPage: true,
  ActionButton: () => <></>,
  TitleComponent: () => <></>,
  BodyComponent: () => <></>,
  ExternalBulkSelector: (<></>),
};

export default AdminDashboardTable;
